import React, { useState } from 'react';
import { useWeb } from '../../context/WebContext';
import { FaTrashAlt, FaPlus, FaTimes, FaSyncAlt, FaEdit } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function ManageGallery() {
  const { gallery, setGallery, services } = useWeb();
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    image: '',
    isBeforeAfter: false,
    beforeImage: '',
    afterImage: ''
  });

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const data = new FormData();
    data.append('file', file);

    const API_URL = import.meta.env.DEV ? 'https://royalblue-beaver-227416.hostingersite.com/api.php' : '/api.php';

    try {
      const res = await fetch(`${API_URL}?action=upload_image`, {
        method: 'POST',
        body: data
      });
      const result = await res.json();
      if (result.success && result.url) {
        setFormData(prev => ({
          ...prev,
          [field]: result.url
        }));
        toast.success("Image uploaded successfully!");
      } else {
        toast.error(result.error || "Failed to upload image");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Error uploading image");
    } finally {
      setUploading(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      title: '',
      category: services[0]?.title || 'Balcony Safety Nets',
      image: '',
      isBeforeAfter: false,
      beforeImage: '',
      afterImage: ''
    });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setFormData({
      title: item.title,
      category: item.category,
      isBeforeAfter: item.isBeforeAfter || false,
      image: item.isBeforeAfter ? '' : item.image,
      beforeImage: item.isBeforeAfter ? item.beforeImage : '',
      afterImage: item.isBeforeAfter ? item.afterImage : ''
    });
    setEditingId(item.id);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    setGallery(prev => prev.filter(item => item.id !== confirmDeleteId));
    toast.success('Gallery item removed!');
    setConfirmDeleteId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (uploading) {
      toast.error('Please wait for the image upload to finish');
      return;
    }

    if (!formData.title || !formData.category) {
      toast.error('Please specify a title and category');
      return;
    }

    if (!formData.isBeforeAfter && !formData.image) {
      toast.error('Please select or upload a Main Image');
      return;
    }

    if (formData.isBeforeAfter && (!formData.beforeImage || !formData.afterImage)) {
      toast.error('Please select or upload both Before and After images');
      return;
    }

    if (editingId) {
      // Editing
      setGallery(prev => prev.map(item => 
        item.id === editingId 
          ? {
              ...item,
              title: formData.title,
              category: formData.category,
              isBeforeAfter: formData.isBeforeAfter,
              image: formData.isBeforeAfter ? formData.afterImage : formData.image,
              beforeImage: formData.isBeforeAfter ? formData.beforeImage : '',
              afterImage: formData.isBeforeAfter ? formData.afterImage : ''
            }
          : item
      ));
      toast.success('Gallery project updated!');
    } else {
      // Adding
      const newItem = {
        id: `g-${Date.now()}`,
        title: formData.title,
        category: formData.category,
        isBeforeAfter: formData.isBeforeAfter,
        image: formData.isBeforeAfter ? formData.afterImage : formData.image,
        beforeImage: formData.isBeforeAfter ? formData.beforeImage : '',
        afterImage: formData.isBeforeAfter ? formData.afterImage : ''
      };
      setGallery(prev => [newItem, ...prev]);
      toast.success('New gallery project added!');
    }
    setModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Manage Installation Gallery</h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">Upload and categorize photos of completed safety net installations.</p>
        </div>
        <button 
          onClick={openAddModal}
          className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-4 py-2.5 rounded-xl transition text-xs sm:text-sm"
        >
          <FaPlus />
          <span>Add Photo</span>
        </button>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {gallery.map((item) => (
          <div key={item.id} className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-sm flex flex-col relative group">
            {/* Action buttons absolute */}
            <div className="absolute top-3 right-3 flex space-x-1.5 z-20">
              <button 
                onClick={() => openEditModal(item)}
                className="bg-blue-600/90 text-white p-2 rounded-lg hover:bg-blue-700 transition shadow"
                title="Edit Photo"
              >
                <FaEdit className="text-xs" />
              </button>
              <button 
                onClick={() => handleDelete(item.id)}
                className="bg-red-600/90 text-white p-2 rounded-lg hover:bg-red-700 transition shadow"
                title="Delete Photo"
              >
                <FaTrashAlt className="text-xs" />
              </button>
            </div>

            <div className="h-44 bg-slate-900 relative">
              <img 
                src={item.image} 
                alt={item.title} 
                className="w-full h-full object-cover" 
              />
              
              {item.isBeforeAfter && (
                <span className="absolute bottom-2 left-2 bg-orange-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shadow flex items-center space-x-1">
                  <FaSyncAlt className="text-[8px]" />
                  <span>Before/After</span>
                </span>
              )}
            </div>
            
            <div className="p-4 flex-grow flex flex-col space-y-1">
              <span className="text-[10px] uppercase font-bold text-orange-600 tracking-wider">
                {item.category}
              </span>
              <h4 className="text-sm font-bold text-slate-900 truncate">
                {item.title}
              </h4>
            </div>
          </div>
        ))}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl p-6 sm:p-8 space-y-6 max-h-[90vh] overflow-y-auto">
            
            <div className="flex justify-between items-center border-b pb-4">
              <h3 className="text-lg font-bold text-slate-900">{editingId ? 'Edit Installation Photo' : 'Add Installation Photo'}</h3>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-650 focus:outline-none"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs sm:text-sm">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Project Name / Caption *</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  placeholder="E.g., Balcony Netting in Highrise Apt"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Service Category *</label>
                <select 
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:border-orange-500 outline-none"
                  required
                >
                  {services.map(s => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                </select>
              </div>

              {/* Before/After Toggle */}
              <div className="flex items-center space-x-3 bg-slate-50 p-4 rounded-xl border">
                <input 
                  type="checkbox" 
                  id="isBeforeAfter"
                  checked={formData.isBeforeAfter}
                  onChange={(e) => setFormData({...formData, isBeforeAfter: e.target.checked})}
                  className="rounded border-slate-300 text-orange-600 focus:ring-orange-500 h-4 w-4"
                />
                <label htmlFor="isBeforeAfter" className="font-semibold text-slate-700 select-none cursor-pointer">
                  This project contains Before & After comparative photos
                </label>
              </div>

              {!formData.isBeforeAfter ? (
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Image File or URL *</label>
                  <div className="flex flex-col gap-2">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleFileUpload(e, 'image')}
                      className="w-full border border-slate-200 rounded-xl px-3 py-1.5 outline-none file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
                      disabled={uploading}
                    />
                    <div className="text-slate-400 text-center text-xs font-bold my-0.5">OR</div>
                    <input 
                      type="url" 
                      value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                      placeholder="Paste image URL directly..."
                      required={!formData.isBeforeAfter && !formData.image}
                      disabled={uploading}
                    />
                  </div>
                  {uploading && <div className="text-xs text-orange-600 font-semibold animate-pulse">Uploading image file...</div>}
                  {formData.image && (
                    <div className="mt-2 h-20 w-32 border rounded-lg overflow-hidden bg-slate-100">
                      <img src={formData.image} alt="Preview" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Before Image *</label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleFileUpload(e, 'beforeImage')}
                      className="w-full border border-slate-200 rounded-xl px-3 py-1.5 outline-none file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
                      disabled={uploading}
                    />
                    <input 
                      type="url" 
                      value={formData.beforeImage}
                      onChange={(e) => setFormData({...formData, beforeImage: e.target.value})}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                      placeholder="Or paste before image URL..."
                      required={formData.isBeforeAfter && !formData.beforeImage}
                      disabled={uploading}
                    />
                    {uploading && <div className="text-xs text-orange-600 font-semibold animate-pulse">Uploading before image...</div>}
                    {formData.beforeImage && (
                      <div className="h-16 w-24 border rounded-lg overflow-hidden bg-slate-100">
                        <img src={formData.beforeImage} alt="Before Preview" className="h-full w-full object-cover" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">After Image *</label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleFileUpload(e, 'afterImage')}
                      className="w-full border border-slate-200 rounded-xl px-3 py-1.5 outline-none file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
                      disabled={uploading}
                    />
                    <input 
                      type="url" 
                      value={formData.afterImage}
                      onChange={(e) => setFormData({...formData, afterImage: e.target.value})}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                      placeholder="Or paste after image URL..."
                      required={formData.isBeforeAfter && !formData.afterImage}
                      disabled={uploading}
                    />
                    {uploading && <div className="text-xs text-orange-600 font-semibold animate-pulse">Uploading after image...</div>}
                    {formData.afterImage && (
                      <div className="h-16 w-24 border rounded-lg overflow-hidden bg-slate-100">
                        <img src={formData.afterImage} alt="After Preview" className="h-full w-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setModalOpen(false)}
                  className="border border-slate-200 rounded-xl px-5 py-2.5 font-semibold text-slate-650 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-2.5 rounded-xl transition"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : (editingId ? 'Save Changes' : 'Upload Photo')}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={() => setConfirmDeleteId(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <FaTrashAlt className="text-red-600 text-lg" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Delete Photo?</h3>
            <p className="text-slate-500 text-sm">This gallery photo will be permanently removed. This action cannot be undone.</p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 border border-slate-200 rounded-xl py-2.5 font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 rounded-xl transition"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
